import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cloud } from "lucide-react";

/**
 * WeatherWidget — displays live weather + today's commercial weather + events.
 */
export default function WeatherWidget({ lat = 48.85, lng = 2.35, only = null, className = "" }) {
  const [current, setCurrent] = useState(null);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    api.get("/weather/current", { params: { lat, lng } }).then(r => setCurrent(r.data)).catch(() => {});
    api.get("/dashboard/sales-weather").then(r => setSales(r.data)).catch(() => {});
  }, [lat, lng]);

  const moodBg = {
    sunny: "from-emerald-500 to-teal-500",
    partly: "from-blue-500 to-sky-500",
    cloudy: "from-amber-500 to-orange-500",
    rainy: "from-rose-500 to-red-500",
  };

  return (
    <div className="contents" data-testid="weather-widget">
      {/* Real weather */}
      <div className={`bg-white border border-slate-200 rounded-2xl p-4 min-w-0 h-auto ${className}`} data-testid="weather-today-card">
        <div className="flex items-center gap-2 mb-2">
          <Cloud className="w-4 h-4 text-slate-500"/>
          <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Météo du jour</div>
        </div>
        {current ? (
          <div>
            <div className="flex items-center gap-4">
              <div className="text-4xl shrink-0">{current.emoji || "❓"}</div>
              <div>
                <div className="font-display text-3xl font-bold text-slate-900">
                  {current.temp != null ? `${current.temp.toFixed(1)}°C` : "—"}
                </div>
                <div className="text-sm text-slate-500">{current.label}</div>
                {current.humidity != null && (
                  <div className="text-[11px] text-slate-400 mt-1">
                    💧 {current.humidity}% · 💨 {current.wind?.toFixed(0) || 0} km/h
                  </div>
                )}
              </div>
            </div>
            {current.events?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Événements du jour</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {current.events.map((e, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      🎉 {e}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : <div className="text-slate-400 text-sm">Chargement…</div>}
      </div>

      {only !== "today" && (
      <div className={`rounded-2xl p-4 text-white bg-gradient-to-br min-w-0 h-auto overflow-hidden ${moodBg[sales?.mood] || "from-slate-500 to-slate-600"}`} data-testid="weather-commercial-card">
        <div className="text-xs font-semibold tracking-wider text-white/80 uppercase mb-2">Météo commerciale</div>
        {sales?.status === "no_data" && <div className="text-white/80 text-sm">Pas encore de données.</div>}
        {sales && sales.status !== "no_data" && (
          <div>
            <div className="flex items-center gap-4">
              <div className="text-4xl shrink-0">{sales.emoji}</div>
              <div className="min-w-0">
                <div className="font-display text-xl font-bold leading-tight break-words">{sales.label}</div>
                <div className="text-sm text-white/80">{sales.weekday} {sales.date ? new Date(sales.date).toLocaleDateString("fr-FR") : ""}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/70">Réalisé</div>
                <div className="font-bold text-sm mt-0.5">{Math.round(sales.realise / 1000)}k€</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/70">Moy. 4 sem.</div>
                <div className="font-bold text-sm mt-0.5">{Math.round(sales.prev_avg / 1000)}k€</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/70">Écart</div>
                <div className="font-bold text-sm mt-0.5">{(sales.delta_pct * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
